<template>
    <v-dialog
        ref="dialogComponent"
        v-model="active"
        :max-width="width"
        :width="dialogWidth"
        :app="app"
        persistent
        :no-click-animation="noClickAnimation"
        :fullscreen="fullscreen"
        :hide-overlay="hideOverlay"
        overlay-opacity="0.8"
        scrollable
        :content-class="dialogContentClass"
        @click:outside="onClickOutside"
    >
        <v-card :class="classList" :style="containerStyle">
            <slot v-if="findSlot('top')" name="top" class="app-dialog__top" />

            <template v-if="!hideHeader && findSlot('header')">
                <slot name="header" />
            </template>
            <template v-else-if="!hideHeader">
                <v-card-title v-if="findSlot('overline')" class="pb-0 d-flex justify-space-between align-start">
                    <slot name="overline" class="text-overline" />
                    <slot v-if="findSlot('overline-actions')" name="overline-actions" />
                </v-card-title>
                <v-card-title v-else-if="overline" class="pb-0 d-flex justify-space-between align-start">
                    <span class="text-overline">{{ overline }}</span>
                    <slot v-if="findSlot('overline-actions')" name="overline-actions" />
                </v-card-title>
                <v-spacer v-else-if="!hideTopSpacer" class="pt-5"></v-spacer>
                <v-card-title
                    v-if="findSlot('title') || title"
                    class="text-h5 pt-0 pb-0 flex-nowrap word-wrap-break-word"
                    :class="cardTitleClassList"
                >
                    <slot v-if="findSlot('title')" name="title" />
                    <template v-else>{{ title }}</template>
                    <v-spacer v-if="findSlot('header-actions')" />
                    <slot v-if="findSlot('header-actions')" name="header-actions" />
                </v-card-title>
            </template>

            <slot v-if="!hideBefore && findSlot('before')" name="before" class="app-dialog__before" />

            <v-card-text v-if="text" :class="textCardClassList">
                <template v-if="Array.isArray(text)">
                    <div v-for="(textLine, index) in text" :key="`text-${index}`">
                        {{ textLine }}
                    </div>
                </template>
                <div v-else>{{ text }}</div>
            </v-card-text>

            <v-card-text
                v-if="findSlot('default')"
                class="px-0 pb-0 app-dialog__body"
                :class="{ 'no-overflow': noContentOverflow }"
                :style="{ 'min-height': minContentHeight }"
            >
                <slot name="default" class="app-dialog__content" />
            </v-card-text>

            <slot v-if="findSlot('after')" name="after" class="app-dialog__after" />

            <v-card-actions v-if="!hideActions">
                <slot v-if="findSlot('actions')" name="actions" v-bind="args" />
                <template v-else>
                    <v-spacer v-if="args.button.no.hide"></v-spacer>
                    <app-button
                        v-if="!args.button.cancel.hide"
                        class="mr-2"
                        secondary
                        :color="args.button.cancel.color"
                        :text="args.button.cancel.text"
                        :disabled="retrying"
                        :icon="args.button.cancel.icon"
                        :test-id="args.button.cancel.testId"
                        @click="args.button.cancel.action"
                    />
                    <v-spacer v-if="!args.button.no.hide"></v-spacer>
                    <app-button
                        v-if="!args.button.no.hide"
                        class="mr-4"
                        secondary
                        :color="args.button.no.color"
                        :text="args.button.no.text"
                        :disabled="retrying"
                        :icon="args.button.no.icon"
                        :test-id="args.button.no.testId"
                        @click="args.button.no.action"
                    />
                    <app-button
                        v-if="!args.button.continue.hide"
                        :text="args.button.continue.text"
                        :tooltip="args.button.continue.tooltip"
                        :color="args.button.continue.color"
                        :loading="args.button.continue.loading || loading || retrying"
                        :disabled="args.button.continue.disabled || disabled || retrying"
                        :icon="args.button.continue.icon"
                        :test-id="args.button.continue.testId"
                        @click="args.button.continue.action"
                    />
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { dialog } from '@core/services/ui';

import i18n from '@plugins/i18n';

export default {
    name: 'AppDialog',
    props: {
        value: {
            type: Boolean,
            default: true
        },
        app: {
            type: Boolean,
            default: false
        },
        persistent: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        size: {
            type: String,
            default: 'medium' // x-small, small, medium, large, x-large, xx-large, dynamic, fullscreen
        },
        color: {
            type: String,
            default: 'primaryText'
        },
        overline: {
            type: String,
            default: null
        },
        title: {
            type: String,
            default: null
        },
        text: {
            type: [String, Array],
            default: null
        },
        cancelButtonText: {
            type: String,
            default: i18n.t('cancel')
        },
        noButtonText: {
            type: String,
            default: i18n.t('no')
        },
        continueButtonText: {
            type: String,
            default: i18n.t('proceed')
        },
        continueButtonTooltip: {
            type: [String, Array],
            default: null
        },
        cancelButtonColor: {
            type: String,
            default: null
        },
        noButtonColor: {
            type: String,
            default: null
        },
        continueButtonColor: {
            type: String,
            default: null
        },
        cancelButtonIcon: {
            type: String,
            default: null
        },
        cancelButtonTestId: {
            type: String,
            default: null
        },
        noButtonIcon: {
            type: String,
            default: null
        },
        noButtonTestId: {
            type: String,
            default: null
        },
        continueButtonIcon: {
            type: String,
            default: null
        },
        continueButtonDisabled: {
            type: Boolean,
            default: false
        },
        continueButtonTestId: {
            type: String,
            default: null
        },
        continueButtonLoading: {
            type: Boolean,
            default: false
        },
        onCancelFn: {
            type: Function,
            default: () => {}
        },
        onNoFn: {
            type: Function,
            default: () => {}
        },
        onContinueFn: {
            type: Function,
            default: () => {}
        },
        afterContinueFn: {
            type: Function,
            default: () => {}
        },
        onErrorFn: {
            type: Function,
            default: () => {}
        },
        hideHeader: {
            type: Boolean,
            default: false
        },
        hideBefore: {
            type: Boolean,
            default: false
        },
        hideActions: {
            type: Boolean,
            default: false
        },
        hideCancelButton: {
            type: Boolean,
            default: false
        },
        hideNoButton: {
            type: Boolean,
            default: true
        },
        hideContinueButton: {
            type: Boolean,
            default: false
        },
        hideOnContinue: {
            type: Boolean,
            default: true
        },
        hideBeforeContinue: {
            type: Boolean,
            default: false
        },
        hideOverlay: {
            type: Boolean,
            default: false
        },
        noClickAnimation: {
            type: Boolean,
            default: false
        },
        showCloseModal: {
            type: Boolean,
            default: false
        },
        hideTopSpacer: {
            type: Boolean,
            default: false
        },
        noContentOverflow: {
            type: Boolean,
            default: false
        },
        minContentHeight: {
            type: String,
            default: null
        },
        fullHeight: {
            type: Boolean,
            default: false
        },
        /**
         * Explicit dialog height (e.g. calc(100vh - 4rem)). When set, width is also applied
         * so the dialog fills the given dimensions rather than shrinking to content.
         */
        height: {
            type: String,
            default: null
        },
        /**
         * Border colour for topBorderClass, e.g. 'var(--v-special-base)'. Mirrors AppPanelCard's
         * accentColor/topBorderClass pair.
         */
        accentColor: {
            type: String,
            default: null
        },
        topBorderClass: {
            type: String,
            default: null
        }
    },
    data: () => ({
        retrying: false
    }),
    computed: {
        args() {
            return {
                color: this.color,
                retrying: this.retrying,
                disabled: this.disabled,
                button: {
                    cancel: {
                        hide: this.hideCancelButton,
                        text: this.cancelButtonText,
                        action: this.onCancel,
                        color: this.cancelButtonColor || 'primaryText',
                        icon: this.cancelButtonIcon,
                        testId: this.cancelButtonTestId
                    },
                    no: {
                        hide: this.hideNoButton,
                        text: this.noButtonText,
                        action: this.onNo,
                        color: this.noButtonColor || 'error',
                        icon: this.noButtonIcon,
                        testId: this.noButtonTestId
                    },
                    continue: {
                        hide: this.hideContinueButton,
                        text: this.continueButtonText,
                        tooltip: this.continueButtonTooltip,
                        action: this.onContinue,
                        color: this.continueButtonColor || this.color,
                        icon: this.continueButtonIcon,
                        disabled: this.continueButtonDisabled,
                        loading: this.continueButtonLoading,
                        testId: this.continueButtonTestId
                    }
                }
            };
        },
        fullscreen() {
            return this.size === 'fullscreen';
        },
        dialogContentClass() {
            const classes = [];

            if (this.fullHeight) {
                classes.push('full-height');
            }

            if (this.height) {
                classes.push('app-dialog--sized');
            }

            if (this.size === 'dynamic') {
                classes.push('app-dialog--dynamic');
            }

            return classes.length ? classes.join(' ') : null;
        },
        dialogWidth() {
            return this.height ? this.width : undefined;
        },
        sizedDialogDimensions() {
            if (!this.height) {
                return null;
            }

            return {
                height: this.height,
                maxHeight: this.height,
                width: this.width,
                maxWidth: this.width,
                margin: '2rem'
            };
        },
        width() {
            let width = '';
            let sizeValue = this.size || 'medium';

            switch (sizeValue) {
                case 'x-small':
                    width = '200';
                    break;
                case 'small':
                    width = '310';
                    break;
                case 'medium':
                    width = '410';
                    break;
                case 'large':
                    width = '570';
                    break;
                case 'x-large':
                    width = '1000';
                    break;
                case 'xx-large':
                    width = '80%';
                    break;
                case 'dynamic':
                    width = undefined;
                    break;
                case 'fullscreen':
                    width = '100%';
                    break;
                default:
                    width = sizeValue;
                    break;
            }

            return width;
        },
        active: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        colorValue() {
            if (!this.color) {
                return '';
            }

            return this._.head(this.color.split(' '));
        },
        shadeValue() {
            if (!this.color) {
                return '';
            }

            const split = this.color.split(' ');

            if (split.length === 2) {
                return this._.last(split);
            }

            return '';
        },
        cardTitleClassList() {
            let classList = [];

            if (this.colorValue) {
                classList.push(this.colorValue + '--text');
            }

            if (this.shadeValue) {
                classList.push('text--' + this.shadeValue);
            }

            return classList.join(' ');
        },
        classList() {
            let classList = ['app-dialog'];

            if (this.disabled) {
                classList.push('app-dialog--disabled');
            }

            if (this.topBorderClass) {
                classList.push(this.topBorderClass);
            }

            return classList.join(' ');
        },
        containerStyle() {
            if (!this.accentColor) {
                return null;
            }

            return `border-top-color: ${this.accentColor} !important`;
        },
        textCardClassList() {
            const classList = ['no-overflow', 'flex-shrink-0', 'app-dialog__text'];

            if (!this.height && this.size !== 'dynamic') {
                classList.push('flex-grow-1');
            }

            return classList;
        }
    },
    watch: {
        active(isActive) {
            if (isActive) {
                this.scheduleSizedDialogDimensions();
            } else {
                this.clearSizedDialogDimensions();
            }
        },
        height() {
            this.scheduleSizedDialogDimensions();
        },
        width() {
            this.scheduleSizedDialogDimensions();
        }
    },
    mounted() {
        if (this.active) {
            this.scheduleSizedDialogDimensions();
        }
    },
    updated() {
        if (this.active && this.height) {
            this.scheduleSizedDialogDimensions();
        }
    },
    methods: {
        getDialogElement() {
            return this.$refs.dialogComponent?.$refs?.dialog ?? null;
        },
        scheduleSizedDialogDimensions() {
            if (!this.height) {
                return;
            }

            this.$nextTick(() => {
                requestAnimationFrame(() => this.applySizedDialogDimensions());
            });
        },
        applySizedDialogDimensions() {
            const el = this.getDialogElement();
            const dimensions = this.sizedDialogDimensions;

            if (!el || !dimensions) {
                return;
            }

            el.style.height = dimensions.height;
            el.style.maxHeight = dimensions.maxHeight;
            el.style.width = dimensions.width;
            el.style.maxWidth = dimensions.maxWidth;
            el.style.margin = dimensions.margin;
        },
        clearSizedDialogDimensions() {
            const el = this.getDialogElement();

            if (!el) {
                return;
            }

            el.style.height = '';
            el.style.maxHeight = '';
            el.style.width = '';
            el.style.maxWidth = '';
            el.style.margin = '';
        },
        onClickOutside($event) {
            if (!this.persistent && !this.showCloseModal) {
                this.active = false;
            }

            this.$emit('click:outside', $event);

            if (this.showCloseModal) {
                dialog({
                    title: this.$t('are_you_sure'),
                    text: 'All unsaved changes will be lost.',
                    onContinueFn: () => (this.active = false),
                    cancelButtonText: 'No, keep editing',
                    continueButtonText: 'Yes, close'
                });
            }
        },
        async onCancel() {
            if (!this.showCloseModal) {
                this.active = false;
                this.$emit('cancel');
                await this.onCancelFn();
                return;
            }

            dialog({
                title: this.$t('are_you_sure'),
                text: 'All unsaved changes will be lost.',
                onContinueFn: async () => {
                    this.active = false;
                    this.$emit('cancel');
                    await this.onCancelFn();
                },
                cancelButtonText: 'No, keep editing',
                continueButtonText: 'Yes, close'
            });
        },
        async onNo() {
            this.active = false;
            this.$emit('no');
            await this.onNoFn();
        },
        async onContinue() {
            // The button's disabled state lags a render behind, so clicks in the same tick reach here.
            if (this.retrying) {
                return;
            }

            if (this.hideBeforeContinue) {
                this.active = false;
            }

            try {
                this.retrying = true;
                await this.onContinueFn();
                await this.afterContinueFn();
            } catch (error) {
                console.error(error);
                await this.onErrorFn(error);
            } finally {
                this.retrying = false;
            }

            if (this.hideOnContinue) {
                this.active = false;
            }

            this.$emit('continue');
        }
    }
};
</script>

<style lang="scss">
.app-dialog {
    .app-form__content {
        font-size: 0.875rem;
        padding: 0 1.8rem !important;
    }
    .v-card__actions {
        padding: 1rem;
        background: var(--v-primaryLight-base);
        border-top: 1px solid rgba(0, 0, 0, 0.05);
    }

    // Vuetify's own scrollable-dialog rule (VDialog.sass) gives *every* direct .v-card__text child
    // `flex: 1 1 auto; overflow-y: auto` - fine for the single app-dialog__body it was designed
    // around, but a #before slot that renders its own v-card-text (as several callers do, for a
    // description/summary above the real scrollable content) becomes a second competitor for that
    // same flex space. On a short viewport the two split the available height roughly evenly instead
    // of the before content sizing to its own (usually much shorter) natural height, so it gets
    // squeezed into a tiny scrollable box that clips its own text. Only app-dialog__body is meant to
    // grow/scroll here; anything else pins to its natural content size instead.
    > .v-card__text:not(.app-dialog__body):not(.app-dialog__text) {
        flex: 0 0 auto !important;
        overflow: visible !important;
    }
}

.v-dialog.app-dialog--sized:not(.v-dialog--fullscreen) {
    max-height: none !important;
}

.v-dialog.app-dialog--dynamic.v-dialog--scrollable {
    display: flex;
    width: max-content !important;
    max-width: calc(100vw - 4rem) !important;
    max-height: calc(100vh - 4rem) !important;
    overflow: hidden !important;

    > .v-card.app-dialog {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        width: max-content !important;
        max-width: 100%;
        min-width: 0;
        min-height: 0;
        max-height: 100%;
        overflow: hidden;

        > .v-card__title,
        > .v-spacer,
        > .v-card__actions {
            flex: 0 0 auto;
        }

        > .v-card__text.app-dialog__text {
            flex: 0 0 auto !important;
        }

        > .v-card__text.app-dialog__body {
            flex: 1 1 auto;
            min-height: 0;
            overflow-x: auto;
            overflow-y: auto;
            padding-top: 1.25rem;
        }
    }
}

.v-dialog.app-dialog--sized.v-dialog--scrollable {
    overflow: hidden !important;

    > .v-card.app-dialog {
        display: flex;
        flex-direction: column;
        width: 100% !important;
        min-width: 0 !important;
        height: 100% !important;
        min-height: 0 !important;
        max-height: 100% !important;
        margin: 0 !important;
        overflow: hidden;

        > .v-card__text {
            overflow-y: hidden;
        }

        > .v-card__text.app-dialog__text {
            flex: 0 0 auto !important;
        }

        > .v-card__text.app-dialog__body {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
        }
    }
}
</style>
